/**
 * Cleared List rows that need a manual pick, split by *why*:
 *   1. The Adesa sheet has no stock number, so VIN matched but stock couldn't.
 *   2. The Adesa stock number and the Tekion record's stock disagree.
 *   3. The stock matched but the VINs differ (riskier — reviewed one at a time).
 *
 * Single-candidate rows show the full Adesa-vs-Tekion comparison inline (no
 * modal needed) with a checkbox for bulk Approve. Multi-candidate rows are
 * ambiguous: no checkbox, a compact row that opens the candidate modal to
 * choose which record. Calm/neutral styling — these are review items, not errors.
 */
import React, { useMemo, useState } from "react"
import { AlertTriangle, ChevronRight } from "lucide-react"

import { WholesaleVehicle } from "../../api/wholesale"
import { THEME } from "../../config/theme"
import { tint } from "../../utils/color"
import { CompactComparison, norm } from "./clearedMatchFields"

interface Props {
  vehicles: WholesaleVehicle[]
  onPick: (vehicle: WholesaleVehicle) => void
  onApprove: (vehicles: WholesaleVehicle[]) => void | Promise<void>
  onExclude: (vehicles: WholesaleVehicle[]) => void | Promise<void>
  busy: boolean
}

// Why a row needs a pick — derived from the row's stock + VIN against its
// candidate Tekion records (no extra backend field needed):
//   missing  — Adesa has no stock number (VIN matched, stock blank)
//   stock    — VIN matched a record, but its stock disagrees
//   vin      — stock matched a record, but its VIN disagrees
type Reason = "missing" | "stock" | "vin"

function reasonFor(v: WholesaleVehicle): Reason {
  if (!norm(v.client)) return "missing"
  const vinMatched = (v.match_candidates ?? []).some((c) => norm(c.tekion_vin) === norm(v.vin))
  return vinMatched ? "stock" : "vin"
}

export default function ClearedValidatorPanel({ vehicles, onPick, onApprove, onExclude, busy }: Props) {
  const missing = vehicles.filter((v) => reasonFor(v) === "missing")
  const stock = vehicles.filter((v) => reasonFor(v) === "stock")
  const vin = vehicles.filter((v) => reasonFor(v) === "vin")

  if (vehicles.length === 0) return null

  return (
    <>
      <ValidatorBox
        vehicles={missing}
        title="Missing stock number on the Adesa sheet"
        subtitle={
          <>
            The <span style={inlineMatchStyle}>VIN matched</span>, but the Adesa row has no stock
            number — review and approve, or open to choose if there's more than one record.
          </>
        }
        onPick={onPick}
        onApprove={onApprove}
        onExclude={onExclude}
        busy={busy}
        bulk
      />
      <ValidatorBox
        vehicles={stock}
        title="Stock numbers disagree"
        subtitle={
          <>
            The <span style={inlineMatchStyle}>VIN matched</span>, but the Adesa stock number
            doesn't match the Tekion record's — review and approve, or open to choose.
          </>
        }
        onPick={onPick}
        onApprove={onApprove}
        onExclude={onExclude}
        busy={busy}
        bulk
      />
      <ValidatorBox
        vehicles={vin}
        title="VIN numbers disagree"
        subtitle="The stock number matched, but the Adesa and Tekion VINs differ — confirm it's the same car, then approve one at a time."
        onPick={onPick}
        onApprove={onApprove}
        onExclude={onExclude}
        busy={busy}
        background={tint(THEME.colors.semantic.danger, 0.08)}
      />
    </>
  )
}

function ValidatorBox({
  vehicles,
  title,
  subtitle,
  onPick,
  onApprove,
  onExclude,
  busy,
  background,
  bulk = false,
}: {
  vehicles: WholesaleVehicle[]
  title: string
  subtitle: React.ReactNode
  onPick: (vehicle: WholesaleVehicle) => void
  onApprove: (vehicles: WholesaleVehicle[]) => void | Promise<void>
  onExclude: (vehicles: WholesaleVehicle[]) => void | Promise<void>
  busy: boolean
  background?: string
  // Checkboxes + Select-all/batch header. Off for the VIN-disagree box, which
  // stays one-at-a-time so mismatched VINs can't be swept up at once.
  bulk?: boolean
}) {
  // Hooks must run every render (before the empty-list early return).
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  // Only single-candidate rows can be batch-approved — a row with multiple
  // candidates is ambiguous and must be resolved in the modal.
  const approvable = useMemo(
    () => vehicles.filter((v) => v.id != null && (v.match_candidates ?? []).length === 1),
    [vehicles],
  )
  const selected = useMemo(
    () => approvable.filter((v) => selectedIds.has(v.id as number)),
    [approvable, selectedIds],
  )
  const allSelected = approvable.length > 0 && selected.length === approvable.length

  if (vehicles.length === 0) return null

  const toggle = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const toggleAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(approvable.map((v) => v.id as number)))

  const handleApprove = async () => {
    if (selected.length === 0 || busy) return
    await onApprove(selected)
    setSelectedIds(new Set())
  }

  const handleExcludeSelected = async () => {
    if (selected.length === 0 || busy) return
    await onExclude(selected)
    setSelectedIds(new Set())
  }

  return (
    <div style={{ ...panelStyle, background: background ?? panelStyle.background }}>
      <div style={headerStyle}>
        <AlertTriangle
          size={16}
          color={THEME.colors.semantic.warning}
          style={{ flexShrink: 0, marginTop: 1 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={titleStyle}>
            {vehicles.length} {vehicles.length === 1 ? "vehicle" : "vehicles"} · {title}
          </div>
          <div style={subtitleStyle}>{subtitle}</div>
        </div>
        {bulk && (
          <div style={actionsStyle}>
            <label style={selectAllStyle}>
              <input
                type="checkbox"
                checked={allSelected}
                disabled={approvable.length === 0}
                onChange={toggleAll}
                style={checkboxStyle}
              />
              Select all
            </label>
            <button
              type="button"
              onClick={handleApprove}
              disabled={selected.length === 0 || busy}
              style={approveButtonStyle(selected.length > 0 && !busy)}
            >
              {busy ? "Approving…" : `Approve${selected.length > 0 ? ` (${selected.length})` : ""}`}
            </button>
            <button
              type="button"
              onClick={handleExcludeSelected}
              disabled={selected.length === 0 || busy}
              style={excludeButtonStyle(selected.length > 0 && !busy)}
            >
              {`Exclude${selected.length > 0 ? ` (${selected.length})` : ""}`}
            </button>
          </div>
        )}
      </div>

      <div style={listStyle}>
        {vehicles.map((v) => {
          const candidate = (v.match_candidates ?? [])[0]
          const single = (v.match_candidates ?? []).length === 1
          const checked = single && v.id != null && selectedIds.has(v.id)

          // Single candidate -> compact inline comparison + per-row Approve
          // (no modal). Checkbox only when the box supports bulk approve.
          if (single && candidate) {
            return (
              <div key={v.id ?? v.adesa_row_order} style={inlineRowStyle}>
                {bulk && (
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => v.id != null && toggle(v.id)}
                    style={{ ...checkboxStyle, marginTop: 2 }}
                    aria-label={`Approve ${v.vin ?? "vehicle"}`}
                  />
                )}
                <CompactComparison
                  vehicle={v}
                  candidate={candidate}
                  action={
                    <div style={rowActionsStyle}>
                      <button
                        type="button"
                        onClick={() => onApprove([v])}
                        disabled={busy}
                        title="Approve this match"
                        style={approveChipButtonStyle(!busy)}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => onExclude([v])}
                        disabled={busy}
                        title="Keep this vehicle out of the cleared list"
                        style={excludeChipButtonStyle(!busy)}
                      >
                        Exclude
                      </button>
                    </div>
                  }
                />
              </div>
            )
          }

          // Multiple (or zero) candidates -> compact row, opens the modal.
          const candidateCount = (v.match_candidates ?? []).length
          return (
            <button
              key={v.id ?? v.adesa_row_order}
              type="button"
              onClick={() => onPick(v)}
              style={compactRowStyle}
            >
              <div>
                <div style={vinStyle}>VIN {v.vin ?? "—"}</div>
                <div style={ymmStyle}>
                  {[v.year, v.make, v.model].filter(Boolean).join(" ") || "Vehicle"} · Stock{" "}
                  {v.client?.trim() || "—"} · {candidateCount} candidate
                  {candidateCount === 1 ? "" : "s"} · choose
                </div>
              </div>
              <ChevronRight size={16} color={THEME.colors.neutral.light.textSecondary} />
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const panelStyle: React.CSSProperties = {
  padding: THEME.spacing.md,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  fontFamily: THEME.fonts.primary,
  marginBottom: THEME.spacing.md,
}

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: THEME.spacing.sm,
  marginBottom: THEME.spacing.sm,
}

const titleStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
}

const subtitleStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: 1,
}

// Light-green highlight for the "VIN matched" phrase — mirrors the matched-
// field chip so the cue reads the same across the feature.
const inlineMatchStyle: React.CSSProperties = {
  background: THEME.colors.success,
  borderRadius: THEME.borderRadius.sm,
  padding: "0 4px",
  fontWeight: 600,
}

const actionsStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: THEME.spacing.md,
  flexShrink: 0,
}

const selectAllStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: THEME.spacing.xs,
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  cursor: "pointer",
  whiteSpace: "nowrap",
}

const checkboxStyle: React.CSSProperties = {
  width: 13,
  height: 13,
  cursor: "pointer",
  accentColor: THEME.colors.navy,
  flexShrink: 0,
  margin: 0,
}

// Batch Exclude — secondary (outline) sibling of the navy batch Approve.
function excludeButtonStyle(enabled: boolean): React.CSSProperties {
  return {
    padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
    fontSize: 13,
    fontWeight: 500,
    color: THEME.colors.neutral.light.textPrimary,
    background: THEME.colors.neutral.light.surface,
    border: `1px solid ${THEME.colors.silver}`,
    borderRadius: THEME.borderRadius.md,
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.5,
    fontFamily: THEME.fonts.primary,
    whiteSpace: "nowrap",
  }
}

// Per-row buttons sit together on the Tekion row's right cell.
const rowActionsStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: THEME.spacing.xs,
}

// Per-row approve — a compact "Approve" pill sized to match the tone chips on
// the Tekion row (same 1px vertical padding / 12px text).
function approveChipButtonStyle(enabled: boolean): React.CSSProperties {
  return {
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.2,
    color: THEME.colors.white,
    backgroundColor: THEME.colors.navy,
    border: "none",
    borderRadius: THEME.borderRadius.sm,
    padding: "1px 8px",
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.5,
    fontFamily: THEME.fonts.primary,
    whiteSpace: "nowrap",
  }
}

// Per-row exclude — outline sibling of the approve pill, same height.
function excludeChipButtonStyle(enabled: boolean): React.CSSProperties {
  return {
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.2,
    color: THEME.colors.neutral.light.textSecondary,
    background: THEME.colors.neutral.light.surface,
    border: `1px solid ${THEME.colors.silver}`,
    borderRadius: THEME.borderRadius.sm,
    padding: "0 8px",
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.5,
    fontFamily: THEME.fonts.primary,
    whiteSpace: "nowrap",
  }
}

function approveButtonStyle(enabled: boolean): React.CSSProperties {
  return {
    padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
    fontSize: 13,
    fontWeight: 500,
    color: THEME.colors.white,
    backgroundColor: THEME.colors.navy,
    border: "none",
    borderRadius: THEME.borderRadius.md,
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.5,
    fontFamily: THEME.fonts.primary,
    whiteSpace: "nowrap",
  }
}

const listStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.sm,
}

const inlineRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: THEME.spacing.sm,
  padding: THEME.spacing.sm,
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.neutral.light.border}`,
  borderRadius: THEME.borderRadius.sm,
}

const compactRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: THEME.spacing.md,
  width: "100%",
  textAlign: "left",
  padding: THEME.spacing.sm,
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.neutral.light.border}`,
  borderRadius: THEME.borderRadius.sm,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}

const vinStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
  fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
}

const ymmStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: 2,
}
